import * as T from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { WEAPONS, moveTown, isEnemy, type ClientMessage, type ModeId, type Ray, type RoomSnapshot, type ShotResultEvent, type TargetKind, type TargetSnapshot, type Vec3, type WeaponId, type PublicPlayer, type EnemyProjectileSnapshot } from '@shooting-range/shared';
import { buildEnvironment, disposeTree, type Environment } from './world.js';
import { buildEnemy, buildTarget, buildWeapon, type WeaponModel } from './models.js';
import { attackerKind, sourceBearing } from './combat-feedback.js';
import { buildEnemyProjectile, positionEnemyProjectile, poseRaider } from './enemy-fire.js';

type Input = Extract<ClientMessage, { type: 'input' }>;
interface RenderTarget { group: T.Group; desired: T.Vector3; snapshot: TargetSnapshot }
interface Effect { mesh: T.Object3D; velocity: T.Vector3; lifetime: number; gravity: boolean }
export class DeadlightScene {
  readonly renderer: T.WebGLRenderer;
  readonly scene = new T.Scene();
  readonly camera = new T.PerspectiveCamera(72, 1, 0.07, 520);
  onShot: ((rays: Ray[], sequence: number) => void) | null = null;
  onInput: ((input: Input) => void) | null = null;
  onReload: (() => void) | null = null;
  onLockChange: ((locked: boolean) => void) | null = null;
  onFootstep: ((sprint: boolean) => void) | null = null;
  private readonly composer: EffectComposer;
  private readonly viewScene = new T.Scene();
  private readonly viewCamera = new T.PerspectiveCamera(62, 1, 0.01, 10);
  private environment: Environment;
  private mode: ModeId = 'town';
  private weaponId: WeaponId = 'rifle';
  private weapon: WeaponModel;
  private snapshot: RoomSnapshot | null = null;
  private playerId = '';
  private active = false;
  private yaw = 0;
  private pitch = 0;
  private kick = 0;
  private flashTime = 0;
  private aiming = false;
  private firing = false;
  private nextShot = 0;
  private predictedAmmo = 30;
  private reloadRequestedAt = 0;
  private keys = new Set<string>();
  private stepTime = 0;
  private lastHealth = 100;
  private readonly clock = new T.Clock();
  private readonly targets = new Map<string, RenderTarget>();
  private readonly players = new Map<string, T.Group>();
  private readonly effects: Effect[] = [];
  private readonly enemyAttacks = new Map<string, { until: number; yaw: number }>();
  private readonly enemyShots = new Map<string, EnemyProjectileSnapshot>();
  private readonly enemyProjectiles = new Map<string, { group: T.Group; snapshot: EnemyProjectileSnapshot }>();
  private authoritativePosition = new T.Vector3();
  private prediction = new T.Vector3();
  private snapshotAt = 0;
  private reduced = false;
  private lastFrameAt = 0;
  private animatePreview = true;
  private previewDirty = true;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new T.WebGLRenderer({ canvas, antialias: localStorage.getItem('deadlight-quality') !== 'low', powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
    this.renderer.shadowMap.enabled = true; this.renderer.shadowMap.type = T.PCFSoftShadowMap;
    this.renderer.toneMapping = T.ACESFilmicToneMapping; this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = T.SRGBColorSpace;
    this.camera.rotation.order = 'YXZ';
    this.environment = buildEnvironment('town'); this.scene.add(this.environment.group); this.setAtmosphere();
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.composer.addPass(new UnrealBloomPass(new T.Vector2(1, 1), 0.22, 0.5, 1.1));
    this.composer.addPass(new OutputPass());
    const studio = new RoomEnvironment(), reflection = new T.PMREMGenerator(this.renderer);
    this.viewScene.environment = reflection.fromScene(studio, 0.04).texture;
    studio.dispose(); reflection.dispose();
    this.viewScene.environmentIntensity = 1.3;
    this.viewScene.add(new T.HemisphereLight(0xf9ecd1, 0x526353, 3));
    const key = new T.DirectionalLight(0xffddb7, 4); key.position.set(-3, 4, -2); this.viewScene.add(key);
    const rim = new T.DirectionalLight(0xb5d4d3, 1.5); rim.position.set(2, 1, 2); this.viewScene.add(rim);
    this.weapon = buildWeapon(this.weaponId); this.viewScene.add(this.weapon.group);
    this.bindInput();
    window.setInterval(() => {
      if (!this.active) return;
      this.onInput?.(this.input());
      if (this.firing && weaponAutomatic(this.weaponId) && this.isPointerLocked()) this.fire();
    }, 50);
    this.resize(); window.addEventListener('resize', () => this.resize());
    this.renderer.setAnimationLoop(() => this.render());
  }

  setMenuAnimation(enabled: boolean): void { this.animatePreview = enabled; this.previewDirty = true; }

  preview(mode: ModeId): void {
    this.previewDirty = true;
    if (this.mode === mode) return;
    this.mode = mode; this.scene.remove(this.environment.group); disposeTree(this.environment.group);
    this.environment = buildEnvironment(mode); this.scene.add(this.environment.group); this.setAtmosphere();
  }
  private setAtmosphere(): void {
    this.scene.background = new T.Color(this.environment.color);
    this.scene.fog = new T.FogExp2(this.environment.color, this.mode === 'attack' ? 0.018 : this.mode === 'town' ? 0.009 : 0.007);
  }
  begin(snapshot: RoomSnapshot, playerId: string): void {
    this.preview(snapshot.settings.modeId); this.active = true; this.playerId = playerId;
    this.yaw = this.pitch = 0; this.keys.clear(); this.nextShot = 0;
    const me = snapshot.players.find(player => player.id === playerId);
    if (me) { this.camera.position.copy(me.position); this.prediction.copy(me.position); this.lastHealth = me.health; this.yaw = me.yaw; this.pitch = me.pitch; }
    this.updateSnapshot(snapshot);
  }
  end(): void {
    this.active = false; this.aiming = false; document.body.classList.remove('scoped', 'aiming'); this.clearInput(); this.snapshot = null;
    if (this.isPointerLocked()) document.exitPointerLock();
    for (const target of this.targets.values()) { this.scene.remove(target.group); disposeTree(target.group); } this.targets.clear();
    for (const player of this.players.values()) { this.scene.remove(player); disposeTree(player); } this.players.clear();
    for (const effect of this.effects) { this.scene.remove(effect.mesh); disposeTree(effect.mesh); } this.effects.length = 0;
    this.enemyAttacks.clear(); this.enemyShots.clear();
    for (const id of this.enemyProjectiles.keys()) this.removeEnemyProjectile(id);
  }
  updateSnapshot(snapshot: RoomSnapshot): void {
    this.snapshot = snapshot; this.snapshotAt = performance.now();
    if (snapshot.settings.weaponId !== this.weaponId) {
      this.viewScene.remove(this.weapon.group); disposeTree(this.weapon.group);
      this.weaponId = snapshot.settings.weaponId; this.weapon = buildWeapon(this.weaponId); this.viewScene.add(this.weapon.group);
    }
    const me = this.me();
    if (me) {
      this.predictedAmmo = me.ammo;
      if (me.reloadEndsAt !== null) this.reloadRequestedAt = 0;
      this.authoritativePosition.copy(me.position);
      if (this.lastHealth <= 0 && me.health > 0 || this.prediction.distanceTo(this.authoritativePosition) > 3) {
        this.prediction.copy(me.position); this.camera.position.copy(me.position); this.yaw = me.yaw; this.pitch = me.pitch;
      } else this.prediction.lerp(this.authoritativePosition, 0.4);
      this.lastHealth = me.health;
    }
    const present = new Set<string>();
    for (const target of snapshot.targets) {
      present.add(target.id); let rendered = this.targets.get(target.id);
      if (!rendered) {
        const group = buildTarget(target); group.position.copy(target.position); this.scene.add(group);
        rendered = { group, desired: new T.Vector3(), snapshot: target }; this.targets.set(target.id, rendered);
      }
      rendered.desired.copy(target.position); rendered.snapshot = target;
    }
    for (const [id, target] of this.targets) if (!present.has(id)) { this.scene.remove(target.group); disposeTree(target.group); this.targets.delete(id); }
    const projectiles = new Set<string>();
    for (const projectile of snapshot.projectiles ?? []) { projectiles.add(projectile.id); this.enemyFired(projectile); }
    for (const id of this.enemyProjectiles.keys()) if (!projectiles.has(id)) this.removeEnemyProjectile(id);
    if (snapshot.phase !== 'active') this.enemyShots.clear();
    const connected = new Set<string>();
    for (const player of snapshot.players) {
      if (player.id === this.playerId || !player.connected || player.spectating) continue;
      connected.add(player.id); let mesh = this.players.get(player.id);
      if (!mesh) { mesh = buildEnemy('raider', true); mesh.position.copy(player.position); mesh.position.y = 0.95; this.players.set(player.id, mesh); this.scene.add(mesh); }
      mesh.visible = player.health > 0;
      mesh.userData.desired = new T.Vector3(player.position.x, 0.95, player.position.z);
      mesh.userData.yaw = player.yaw;
    }
    for (const [id, mesh] of this.players) if (!connected.has(id)) { this.scene.remove(mesh); disposeTree(mesh); this.players.delete(id); }
  }
  requestPointerLock(): void {
    if ('requestPointerLock' in this.canvas) void this.canvas.requestPointerLock()?.catch(() => this.onLockChange?.(false));
  }
  isPointerLocked(): boolean { return document.pointerLockElement === this.canvas; }
  setQuality(low: boolean): void {
    this.reduced = low; this.renderer.setPixelRatio(Math.min(devicePixelRatio, low ? 0.75 : 1.5)); this.renderer.shadowMap.enabled = !low; this.resize();
  }
  private me(): PublicPlayer | undefined { return this.snapshot?.players.find(player => player.id === this.playerId); }
  private canAct(): boolean { const me = this.me(); return this.active && this.snapshot?.phase === 'active' && !!me && me.health > 0 && !me.spectating && !me.extracted; }
  private input(): Input {
    const enabled = this.canAct() && this.isPointerLocked();
    return { type: 'input', forward: enabled ? Number(this.keys.has('KeyW') || this.keys.has('ArrowUp')) - Number(this.keys.has('KeyS') || this.keys.has('ArrowDown')) : 0,
      strafe: enabled ? Number(this.keys.has('KeyD') || this.keys.has('ArrowRight')) - Number(this.keys.has('KeyA') || this.keys.has('ArrowLeft')) : 0,
      yaw: this.yaw, pitch: this.pitch, sprint: enabled && this.keys.has('ShiftLeft') && !this.aiming };
  }
  private clearInput(): void { this.keys.clear(); this.firing = this.aiming = false; this.onInput?.(this.input()); }
  private bindInput(): void {
    document.addEventListener('pointerlockchange', () => { if (!this.isPointerLocked()) this.clearInput(); this.onLockChange?.(this.isPointerLocked()); });
    document.addEventListener('mousemove', event => {
      if (!this.isPointerLocked()) return;
      const sensitivity = this.aiming ? this.weaponId === 'sniper' ? 0.00045 : 0.0011 : 0.002;
      this.yaw = T.MathUtils.euclideanModulo(this.yaw - event.movementX * sensitivity + Math.PI, Math.PI * 2) - Math.PI;
      if (this.mode === 'classic') this.yaw = T.MathUtils.clamp(this.yaw, -1.2, 1.2);
      this.pitch = T.MathUtils.clamp(this.pitch - event.movementY * sensitivity, -1.3, 1.3);
    });
    document.addEventListener('keydown', event => {
      if (!this.isPointerLocked()) return;
      if (event.code === 'Escape') { document.exitPointerLock(); return; }
      if (['Tab', 'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)) event.preventDefault();
      this.keys.add(event.code); this.onInput?.(this.input());
      if (event.code === 'KeyR' && !event.repeat) this.reload();
    });
    document.addEventListener('keyup', event => { this.keys.delete(event.code); if (this.active) this.onInput?.(this.input()); });
    document.addEventListener('mousedown', event => {
      if (!this.isPointerLocked()) return;
      if (event.button === 0) { this.firing = true; this.fire(); }
      if (event.button === 2) this.aiming = true;
    });
    document.addEventListener('mouseup', event => { if (event.button === 0) this.firing = false; if (event.button === 2) this.aiming = false; });
    document.addEventListener('contextmenu', event => { if (this.active) event.preventDefault(); });
    window.addEventListener('blur', () => this.clearInput());
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.clearInput(); });
  }
  private reload(): void {
    const me = this.me(); if (!this.canAct() || !me || me.reloadEndsAt !== null || me.ammo >= WEAPONS[this.weaponId].magazine || performance.now() - this.reloadRequestedAt < 500) return;
    this.reloadRequestedAt = performance.now(); this.onReload?.();
  }
  private fire(): void {
    const me = this.me(), now = performance.now(), weapon = WEAPONS[this.weaponId];
    if (!this.canAct() || !me || now < this.nextShot || me.reloadEndsAt !== null || now - this.reloadRequestedAt < 500) return;
    if (this.predictedAmmo <= 0) { this.reload(); return; }
    this.predictedAmmo--; this.nextShot = now + weapon.cadenceMs + 5;
    const direction = new T.Vector3(); this.camera.getWorldDirection(direction);
    const origin = { ...me.position }; // Wire origins reconcile to server history, never a claimed teleport.
    const rays: Ray[] = [{ origin, direction: { x: direction.x, y: direction.y, z: direction.z } }];
    const right = new T.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion), up = new T.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion);
    this.kick = weapon.recoil; this.flashTime = now + 45;
    this.tracer(this.camera.position.clone().addScaledVector(right, 0.2).addScaledVector(up, -0.2), this.camera.position.clone().addScaledVector(direction, 90), 0xf9d39a);
    this.onShot?.(rays, Date.now() * 1000 + Math.floor(now % 1000));
  }
  confirmShot(result: ShotResultEvent): TargetKind | 'miss' {
    if (!result.accepted || !result.hitTargetIds.length) return 'miss';
    const kind = this.targets.get(result.hitTargetIds[0]!)?.snapshot.kind ?? 'steel';
    for (const point of result.impactPoints) this.burst(point, isEnemy(kind) ? 0x98a577 : 0xfbe0a5, 7);
    return kind;
  }
  destroyEffect(position: Vec3, kind: TargetKind): void { this.burst(position, isEnemy(kind) ? 0x8e9970 : 0xd5c4a4, 18); }
  bearingTo(source: Vec3) { return sourceBearing(source, this.camera.position, this.yaw); }
  enemyAttack(targetId: string, origin: Vec3, destination: Vec3): ReturnType<typeof attackerKind> {
    const kind = attackerKind(this.targets.get(targetId)?.snapshot.kind, origin);
    if (kind !== 'raider') this.enemyAttacks.set(targetId, { until: performance.now() + 450, yaw: Math.atan2(origin.x - destination.x, origin.z - destination.z) });
    return kind;
  }
  enemyFired(projectile: EnemyProjectileSnapshot): void {
    const latest = this.enemyShots.get(projectile.targetId);
    if (!latest || latest.firedAt < projectile.firedAt) this.enemyShots.set(projectile.targetId, projectile);
    if (!this.enemyProjectiles.has(projectile.id)) {
      const group = buildEnemyProjectile(); this.scene.add(group);
      this.enemyProjectiles.set(projectile.id, { group, snapshot: projectile });
      positionEnemyProjectile(group, projectile, projectile.firedAt);
    }
  }
  enemyImpact(id: string, position: Vec3, surface: 'cover' | 'player'): void {
    this.removeEnemyProjectile(id);
    if (surface === 'cover') this.burst(position, 0xffd49c, 9);
  }
  private removeEnemyProjectile(id: string): void {
    const projectile = this.enemyProjectiles.get(id); if (!projectile) return;
    this.scene.remove(projectile.group); disposeTree(projectile.group); this.enemyProjectiles.delete(id);
  }
  tracer(from: Vec3, to: Vec3, color = 0xff9265, lifetime = 0.075): void {
    const mesh = new T.Line(new T.BufferGeometry().setFromPoints([new T.Vector3().copy(from), new T.Vector3().copy(to)]), new T.LineBasicMaterial({ color, transparent: true, opacity: lifetime > 0.1 ? 0.95 : 0.65, toneMapped: false }));
    this.scene.add(mesh); this.effects.push({ mesh, velocity: new T.Vector3(), lifetime, gravity: false });
  }
  private burst(point: Vec3, color: number, count: number): void {
    if (this.effects.length > 260) return;
    for (let i = 0; i < count; i++) {
      const mesh = new T.Mesh(new T.TetrahedronGeometry(0.025 + Math.random() * 0.055), new T.MeshBasicMaterial({ color })); mesh.position.copy(point); this.scene.add(mesh);
      this.effects.push({ mesh, velocity: new T.Vector3((Math.random() - 0.5) * 4, 1 + Math.random() * 3, (Math.random() - 0.5) * 4), lifetime: 0.4 + Math.random() * 0.6, gravity: true });
    }
  }
  private render(): void {
    const frameAt = performance.now();
    if (!this.active && !this.animatePreview && !this.previewDirty) return;
    const fps = this.active ? this.isPointerLocked() ? 60 : 3 : this.reduced ? 12 : 24;
    if (document.hidden || frameAt - this.lastFrameAt < 1000 / fps) return;
    this.previewDirty = false;
    this.lastFrameAt = frameAt;
    const dt = Math.min(this.clock.getDelta(), 0.05), now = performance.now(), smoothing = 1 - Math.exp(-dt * 16);
    const me = this.me(); const input = this.input();
    if (this.active && me) {
      if (this.mode === 'town' && this.canAct()) {
        if (now - this.snapshotAt < 500) this.prediction.copy(moveTown(this.prediction, input.forward, input.strafe, input.yaw, input.sprint, dt));
        this.camera.position.lerp(this.prediction, smoothing);
      } else this.camera.position.lerp(this.authoritativePosition, smoothing);
      const moving = this.mode === 'town' && (input.forward !== 0 || input.strafe !== 0) && this.canAct();
      if (moving && now - this.stepTime > (input.sprint ? 280 : 420)) { this.onFootstep?.(input.sprint); this.stepTime = now; }
      this.camera.position.y = me.health <= 0 ? 0.7 : 1.68 + (moving && !this.reduced ? Math.sin(now * (input.sprint ? 0.023 : 0.016)) * 0.025 : 0);
      this.camera.rotation.set(this.pitch + this.kick * 0.13, this.yaw, 0);
      const scoped = this.aiming && this.weaponId === 'sniper' && this.canAct();
      this.camera.fov = T.MathUtils.lerp(this.camera.fov, scoped ? 20 : this.aiming ? 52 : input.sprint && moving ? 79 : 72, smoothing);
      this.camera.updateProjectionMatrix();
      document.body.classList.toggle('scoped', scoped);
      document.body.classList.toggle('aiming', this.aiming);
      this.weapon.group.visible = me.health > 0 && !me.spectating && !me.extracted && !scoped;
      const reloading = me.reloadEndsAt !== null || now - this.reloadRequestedAt < 500;
      const sway = Math.sin(now * 0.0014) * 0.005 + (moving ? Math.sin(now * 0.015) * 0.01 : 0);
      const aimX = this.aiming ? 0 : 0.24, aimY = this.aiming ? -0.13 : -0.24;
      this.weapon.group.position.lerp(new T.Vector3(aimX + sway, aimY - (reloading ? 0.25 : 0), -0.65 + this.kick * 0.7), smoothing);
      this.weapon.group.rotation.set(-this.kick * 0.65 - (reloading ? 0.4 : 0), reloading ? -0.45 : -0.025, reloading ? -0.6 : sway * 0.5);
      this.weapon.slide.position.z = this.kick * 0.2;
      this.weapon.flash.visible = now < this.flashTime; this.weapon.light.intensity = now < this.flashTime ? 3 : 0;
      this.kick = T.MathUtils.lerp(this.kick, 0, 1 - Math.exp(-dt * 15));
    } else {
      this.camera.position.set(this.mode === 'town' ? -3 : 3, 2.3, this.mode === 'town' ? -3 : 7);
      this.camera.rotation.set(-0.02, Math.sin(now * 0.00008) * 0.055 + (this.mode === 'town' ? -0.08 : 0), 0);
      this.camera.fov = 65; this.camera.updateProjectionMatrix();
    }
    const serverNow = (this.snapshot?.serverTime ?? 0) + now - this.snapshotAt;
    for (const [id, shot] of this.enemyShots) if (serverNow - shot.firedAt > 700) this.enemyShots.delete(id);
    for (const [id, projectile] of this.enemyProjectiles) {
      positionEnemyProjectile(projectile.group, projectile.snapshot, serverNow);
      if (serverNow > projectile.snapshot.expiresAt) this.removeEnemyProjectile(id);
    }
    for (const [id, attack] of this.enemyAttacks) if (now >= attack.until) this.enemyAttacks.delete(id);
    for (const { group, desired, snapshot } of this.targets.values()) {
      group.position.lerp(desired, smoothing);
      if (isEnemy(snapshot.kind)) {
        if (snapshot.health > 0 && snapshot.kind === 'raider') {
          poseRaider(group, snapshot, serverNow, this.camera.position, this.enemyShots.get(snapshot.id));
        } else if (snapshot.health > 0) {
          const attack = this.enemyAttacks.get(snapshot.id), pulse = attack ? Math.max(0, (attack.until - now) / 450) : 0;
          const velocity = snapshot.velocity;
          if (attack) group.rotation.y = attack.yaw;
          else if (Math.hypot(velocity.x, velocity.z) > 0.1) group.rotation.y = Math.atan2(-velocity.x, -velocity.z);
          const crawler = snapshot.kind === 'crawler', gait = crawler ? 0.018 : 0.009, stride = crawler ? 0.32 : 0.6;
          const limbs = group.userData.limbs as T.Group[]; limbs.forEach((limb, i) => {
            const phase = crawler ? (i === 0 || i === 3 ? 0 : Math.PI) : i * Math.PI;
            limb.rotation.x = Math.sin(now * gait + phase) * stride - (crawler ? (i % 2 === 0 ? pulse * 0.45 : 0) : (i % 2 ? pulse * 1.25 : 0));
          });
          group.rotation.x = -pulse * (crawler ? 0.12 : 0.22);
          group.rotation.z = Math.sin(now * 0.005 + group.position.x) * 0.03;
        } else {
          if (snapshot.kind === 'raider') {
            const tilt = group.rotation.z;
            poseRaider(group, { ...snapshot, aim: null }, serverNow, this.camera.position);
            group.rotation.z = tilt;
          }
          group.rotation.z = T.MathUtils.lerp(group.rotation.z, 1.5, smoothing);
        }
      } else if (snapshot.health <= 0) group.rotation.x = T.MathUtils.lerp(group.rotation.x, -Math.PI / 2, smoothing);
    }
    for (const mesh of this.players.values()) {
      const desired = mesh.userData.desired as T.Vector3; const moving = desired.distanceTo(mesh.position) > 0.05;
      mesh.position.lerp(desired, smoothing); mesh.rotation.y = mesh.userData.yaw as number;
      (mesh.userData.limbs as T.Group[]).forEach((limb, i) => limb.rotation.x = moving ? Math.sin(now * 0.012 + i * Math.PI) * 0.5 : 0);
    }
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const effect = this.effects[i]!; effect.lifetime -= dt; effect.mesh.position.addScaledVector(effect.velocity, dt);
      if (effect.gravity) effect.velocity.y -= dt * 8;
      if (effect.lifetime <= 0) { this.scene.remove(effect.mesh); disposeTree(effect.mesh); this.effects.splice(i, 1); }
    }
    const sun = this.environment.sun; sun.position.set(this.camera.position.x - 32, 32, this.camera.position.z - 35); sun.target.position.set(this.camera.position.x, 0, this.camera.position.z - 20);
    this.environment.dust.position.z = this.camera.position.z; this.environment.dust.rotation.y = Math.sin(now * 0.0001) * 0.15;
    if (this.reduced) this.renderer.render(this.scene, this.camera); else this.composer.render();
    if (this.active && this.weapon.group.visible) {
      this.renderer.autoClear = false; this.renderer.clearDepth(); this.renderer.render(this.viewScene, this.viewCamera); this.renderer.autoClear = true;
    }
  }
  private resize(): void {
    this.previewDirty = true;
    const width = this.canvas.clientWidth || window.innerWidth, height = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(width, height, false); this.composer.setSize(width, height);
    this.camera.aspect = this.viewCamera.aspect = width / height; this.camera.updateProjectionMatrix(); this.viewCamera.updateProjectionMatrix();
  }
}
const weaponAutomatic = (id: WeaponId): boolean => WEAPONS[id].automatic;
